import { useEffect, useMemo, useState } from 'react';
import type { PaymentMethod, SalesReport } from '@equinox/shared';
import { formatCents } from '@equinox/shared';
import { useI18n } from '../../i18n/I18nContext.js';
import { reportsApi, staffApi, terminalsApi } from '../../api/resources.js';
import { ApiError } from '../../lib/apiClient.js';
import type { AdminStaff, AdminTerminal } from '../../api/types.js';
import { Banner } from '../../components/Banner.js';
import { StatTile } from '../../components/StatTile.js';
import { BarList } from '../../components/charts/BarList.js';
import { daysAgoISO, startOfMonthISO, todayISO } from '../../lib/format.js';

type Period = 'today' | 'week' | 'month' | 'custom';

function rangeForPeriod(period: Period, customFrom: string, customTo: string): { from: string; to: string } {
  const to = todayISO();
  if (period === 'today') return { from: to, to };
  if (period === 'week') return { from: daysAgoISO(7), to };
  if (period === 'month') return { from: startOfMonthISO(), to };
  return { from: customFrom, to: customTo };
}

export function ReportsPage() {
  const { t } = useI18n();
  const [period, setPeriod] = useState<Period>('today');
  const [customFrom, setCustomFrom] = useState(todayISO());
  const [customTo, setCustomTo] = useState(todayISO());
  const [method, setMethod] = useState<PaymentMethod | ''>('');
  const [staffId, setStaffId] = useState('');
  const [terminalId, setTerminalId] = useState('');

  const [staff, setStaff] = useState<AdminStaff[]>([]);
  const [terminals, setTerminals] = useState<AdminTerminal[]>([]);
  const [report, setReport] = useState<SalesReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const range = useMemo(() => rangeForPeriod(period, customFrom, customTo), [period, customFrom, customTo]);

  useEffect(() => {
    void Promise.all([staffApi.list(), terminalsApi.list()]).then(([s, tm]) => {
      setStaff(s);
      setTerminals(tm);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    reportsApi
      .sales({
        from: range.from,
        to: range.to,
        method: method || undefined,
        staffId: staffId || undefined,
        terminalId: terminalId || undefined,
      })
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : t('common.error'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range.from, range.to, method, staffId, terminalId]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div className="page-header">
        <div>
          <h1>{t('reports.title')}</h1>
          <p>{t('reports.subtitle')}</p>
        </div>
      </div>

      <div className="card toolbar">
        <select value={period} onChange={(e) => setPeriod(e.target.value as Period)} aria-label={t('reports.title')}>
          <option value="today">{t('reports.period.today')}</option>
          <option value="week">{t('reports.period.week')}</option>
          <option value="month">{t('reports.period.month')}</option>
          <option value="custom">{t('reports.period.custom')}</option>
        </select>
        {period === 'custom' && (
          <>
            <label htmlFor="report-from">
              {t('reports.from')}
              <input id="report-from" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            </label>
            <label htmlFor="report-to">
              {t('reports.to')}
              <input id="report-to" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            </label>
          </>
        )}
        <select
          value={method}
          onChange={(e) => setMethod(e.target.value as PaymentMethod | '')}
          aria-label={t('reports.filterMethod')}
        >
          <option value="">{t('reports.filterMethod')}: {t('common.all')}</option>
          <option value="cash">{t('reports.methodCash')}</option>
          <option value="card">{t('reports.methodCard')}</option>
        </select>
        <select value={staffId} onChange={(e) => setStaffId(e.target.value)} aria-label={t('reports.filterStaff')}>
          <option value="">{t('reports.filterStaff')}: {t('common.all')}</option>
          {staff.map((s) => (
            <option key={s.id} value={s.id}>
              {s.displayName}
            </option>
          ))}
        </select>
        <select
          value={terminalId}
          onChange={(e) => setTerminalId(e.target.value)}
          aria-label={t('reports.filterTerminal')}
        >
          <option value="">{t('reports.filterTerminal')}: {t('common.all')}</option>
          {terminals.map((tm) => (
            <option key={tm.id} value={tm.id}>
              {tm.name}
            </option>
          ))}
        </select>
      </div>

      {error && <Banner kind="danger">{error}</Banner>}

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : !report || report.transactionCount === 0 ? (
        <p>{t('reports.noData')}</p>
      ) : (
        <>
          <div className="stat-grid">
            <StatTile label={t('reports.grossSales')} value={formatCents(report.grossSalesCents)} />
            <StatTile label={t('reports.netSales')} value={formatCents(report.netSalesCents)} />
            <StatTile label={t('reports.discountTotal')} value={formatCents(report.discountTotalCents)} />
            <StatTile label={t('reports.transactions')} value={String(report.transactionCount)} />
            <StatTile label={t('reports.itemsSold')} value={String(report.itemsSold)} />
            <StatTile label={t('reports.averageTicket')} value={formatCents(report.averageTicketCents)} />
            <StatTile label={t('reports.costOfGoods')} value={formatCents(report.costOfGoodsCents)} />
            <StatTile label={t('reports.netProfit')} value={formatCents(report.netProfitCents)} />
          </div>

          {report.itemsWithoutCost > 0 && (
            <p className="field-hint">
              {t('reports.itemsWithoutCost', { count: report.itemsWithoutCost })}
            </p>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 'var(--space-6)' }}>
            <div className="card">
              <h2>{t('reports.byMethod')}</h2>
              <BarList
                emptyLabel={t('reports.noData')}
                items={report.byPaymentMethod.map((m, i) => ({
                  key: m.method,
                  label: m.method === 'cash' ? t('reports.methodCash') : t('reports.methodCard'),
                  value: m.amountCents,
                  displayValue: formatCents(m.amountCents),
                  color: i === 0 ? 'var(--chart-series-1)' : 'var(--chart-series-2)',
                }))}
              />
            </div>

            <div className="card">
              <h2>{t('reports.byReason')}</h2>
              <BarList
                emptyLabel={t('reports.noData')}
                seriesLabel={t('reports.discountTotal')}
                items={report.discountByReason.map((r, i) => ({
                  key: r.reasonId ?? `no-reason-${i}`,
                  label: r.label,
                  value: r.amountCents,
                  displayValue: formatCents(r.amountCents),
                }))}
              />
            </div>

            <div className="card">
              <h2>{t('reports.topByUnits')}</h2>
              <BarList
                emptyLabel={t('reports.noData')}
                seriesLabel={t('reports.itemsSold')}
                items={report.topItemsByUnits.map((it, i) => ({
                  key: `${it.productId ?? it.sku}-${i}`,
                  label: it.name,
                  value: it.unitsSold,
                  displayValue: String(it.unitsSold),
                }))}
              />
            </div>

            <div className="card">
              <h2>{t('reports.topByRevenue')}</h2>
              <BarList
                emptyLabel={t('reports.noData')}
                seriesLabel={t('reports.netSales')}
                items={report.topItemsByRevenue.map((it, i) => ({
                  key: `${it.productId ?? it.sku}-${i}`,
                  label: it.name,
                  value: it.revenueCents,
                  displayValue: formatCents(it.revenueCents),
                }))}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
